import { useState, useRef, useMemo, useEffect } from "react";
import { StyleSheet, View, Pressable } from "react-native";
import { router, usePathname } from "expo-router";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Search from "@/components/Search";
import useContextMenu from "@/hooks/useContextMenu";
import { useSMS } from "@/hooks/useSMS";
import { useToolTip } from "@/hooks/useToolTip";
import { MaterialCommunityIcons } from "@expo/vector-icons";

interface ChatDrawerHeaderProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
  onModeChange?: (mode: "chat" | "sms", phoneNumber?: string) => void;
}

export function ChatDrawerHeader({ searchValue, setSearchValue, onModeChange }: ChatDrawerHeaderProps) {
  const { colors } = useTheme();
  const { phones, unreadCounts, totalUnread } = useSMS();
  const contextMenu = useContextMenu();
  const toolTip = useToolTip();
  const enumButtonRef = useRef<View>(null);
  const searchButtonRef = useRef<View>(null);
  const newMessageButtonRef = useRef<View>(null);
  const pathname = usePathname();
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [selectedOption, setSelectedOption] = useState<{ label: string; number: string | null }>({
    label: "Messages",
    number: null,
  });

  // Sync selected option with current route
  useEffect(() => {
    if (pathname.startsWith("/chat/sms/")) {
      const match = pathname.match(/\/chat\/sms\/([^/]+)/);
      if (match) {
        const phoneNumber = decodeURIComponent(match[1]);
        const matchingPhone = phones.find((p) => p.number === phoneNumber);
        if (matchingPhone) {
          setSelectedOption({ label: matchingPhone.displayName, number: matchingPhone.number });
          return;
        }
        setSelectedOption({ label: phoneNumber, number: phoneNumber });
        return;
      }
    }
    setSelectedOption({ label: "Messages", number: null });
  }, [pathname, phones]);

  // Build enum options
  const enumOptions = useMemo(() => {
    return phones.map((phone) => ({
      label: phone.displayName,
      number: phone.number,
      unread: unreadCounts[phone.number] || 0,
    }));
  }, [phones, unreadCounts]);

  const handleOptionSelect = (option: { label: string; number: string | null }) => {
    setSelectedOption(option);
    contextMenu.setVisible(false);
    hideTooltip();

    if (option.number) {
      const encodedPhone = encodeURIComponent(option.number);
      router.push(`/chat/sms/${encodedPhone}`);
      onModeChange?.("sms", option.number);
    } else {
      router.push("/chat");
      onModeChange?.("chat");
    }
  };

  const showTooltip = (ref: React.RefObject<View | null>, text: string) => {
    toolTip.setToolTipContent(
      <View style={[{ backgroundColor: colors.background, borderColor: colors.border }, styles.tooltip]}>
        <ThemedText>{text}</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((pageX, pageY) => {
      toolTip.setLocation({ x: pageX, y: pageY - 36 } as any);
      toolTip.setVisible(true);
    });
  };

  const hideTooltip = () => {
    toolTip.setVisible(false);
  };

  const LABEL_TRUNCATE_LENGTH = 25; // "Client Coordinator (+1 4"

  const showDropdownTooltip = () => {
    if (selectedOption.label.length <= LABEL_TRUNCATE_LENGTH) return;
    showTooltip(enumButtonRef, selectedOption.label);
  };

  const openMenu = () => {
    hideTooltip();
    const menuContent = (
      <View style={[styles.enumMenu, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {enumOptions.map((option) => (
          <Pressable
            key={option.number}
            onPress={() => handleOptionSelect(option)}
            style={({ pressed, hovered }) => [
              styles.enumMenuItem,
              hovered && { backgroundColor: colors.background },
              pressed && { opacity: 0.7 },
            ]}
          >
            <View style={styles.enumMenuItemContent}>
              <MaterialCommunityIcons name="phone" size={18} color={colors.textSecondary} />
              <ThemedText style={styles.enumMenuItemText} numberOfLines={1}>
                {option.label}
              </ThemedText>
            </View>
            {option.unread > 0 && (
              <View style={[styles.unreadBadge, { backgroundColor: colors.primary }]}>
                <ThemedText style={styles.unreadBadgeText}>{option.unread > 99 ? "99+" : option.unread}</ThemedText>
              </View>
            )}
          </Pressable>
        ))}
      </View>
    );

    contextMenu.setIgnoreRef(enumButtonRef);
    enumButtonRef.current?.measureInWindow((pageX, pageY) => contextMenu.setLocation({ x: pageX - 8, y: pageY + 36 }));
    contextMenu.setMenuContent(menuContent);
    contextMenu.setVisible(true);
    contextMenu.setStyle({ backgroundColor: colors.card, overflow: "hidden", borderRadius: 8 });
  };

  const handleSearchToggle = () => {
    hideTooltip();
    setIsSearchMode(true);
  };

  const handleSearchCancel = () => {
    setIsSearchMode(false);
    setSearchValue("");
  };

  const isInSMSMode = pathname.startsWith("/chat/sms/");
  const currentSMSPhone = useMemo(() => {
    if (!isInSMSMode) return null;
    const match = pathname.match(/\/chat\/sms\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }, [pathname, isInSMSMode]);

  if (isSearchMode) {
    return (
      <View style={[styles.header, { borderColor: colors.border }]}>
        <Button icon="arrow-left" onPress={handleSearchCancel} style={styles.iconButton} />
        <Search
          placeholder="Search conversations"
          autoFocus={true}
          value={searchValue}
          setValue={setSearchValue}
          style={styles.search}
        />
      </View>
    );
  }

  return (
    <View style={[styles.header, { borderColor: colors.border }]}>
      {/* Dropdown - takes available space */}
      <Pressable
        ref={enumButtonRef}
        onPress={phones.length > 0 ? openMenu : undefined}
        onPointerEnter={showDropdownTooltip}
        onPointerLeave={hideTooltip}
        style={({ pressed, hovered }) => [
          styles.dropdown,
          { backgroundColor: hovered ? colors.background : "transparent" },
          pressed && { opacity: 0.7 },
        ]}
      >
        <ThemedText style={styles.dropdownText} numberOfLines={1}>
          {selectedOption.label}
        </ThemedText>
        {phones.length > 0 && <MaterialCommunityIcons name="chevron-down" size={18} color={colors.textSecondary} />}
        {totalUnread > 0 && (
          <View style={[styles.totalUnreadBadge, { backgroundColor: colors.primary }]}>
            <ThemedText style={styles.unreadBadgeText}>{totalUnread > 99 ? "99+" : totalUnread}</ThemedText>
          </View>
        )}
      </Pressable>

      {/* Action buttons - fixed width */}
      <View style={styles.actions}>
        <View
          ref={searchButtonRef}
          onPointerEnter={() => showTooltip(searchButtonRef, "Search")}
          onPointerLeave={hideTooltip}
        >
          <Button icon="card-search" onPress={handleSearchToggle} style={styles.iconButton} />
        </View>
        {isInSMSMode && currentSMSPhone && (
          <View
            ref={newMessageButtonRef}
            onPointerEnter={() => showTooltip(newMessageButtonRef, "New Message")}
            onPointerLeave={hideTooltip}
          >
            <Button
              icon="message-plus"
              onPress={() => {
                hideTooltip();
                router.push(`/chat/sms/${encodeURIComponent(currentSMSPhone)}/new`);
              }}
              style={styles.iconButton}
            />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    gap: 8,
  },
  dropdown: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 6,
    minWidth: 0, // Allow shrinking
  },
  dropdownText: {
    fontSize: 17,
    fontWeight: "600",
    flexShrink: 1, // Allow text to shrink/truncate
  },
  actions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexShrink: 0, // Don't shrink action buttons
  },
  iconButton: {
    width: 36,
    height: 36,
    minWidth: 36,
    paddingHorizontal: 0,
  },
  search: {
    flex: 1,
    marginBottom: 0,
    borderRadius: 8,
  },
  totalUnreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 6,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  enumMenu: {
    minWidth: 220,
    maxWidth: 300,
    borderRadius: 8,
    overflow: "hidden",
  },
  enumMenuItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 8,
  },
  enumMenuItemContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  enumMenuItemText: {
    fontSize: 14,
    flexShrink: 1,
  },
  unreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 6,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  unreadBadgeText: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "700",
  },
  tooltip: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
});
